import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, OneGoHeaderComponent } from 'onego-header';
import { EMPTY, combineLatest } from 'rxjs';
import { catchError, startWith, tap } from 'rxjs/operators';

import buildVersion from '../assets/build-version.json';
import { environment } from '../environments/environment';
import { SocketOverlayComponent } from './mtp-common/components/modals/socket-overlay/socket-overlay.component';
import { AppStateMonitorComponent } from './mtp-common/components/util/app-state-monitor/app-state-monitor.component';
import { AppSettingsService, TenantService, UserService } from './mtp-common/services';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [OneGoHeaderComponent, RouterOutlet, SocketOverlayComponent, AppStateMonitorComponent],
})
export class AppComponent implements OnInit {
    protected menu: Array<MenuItem> = [];
    protected title: string;
    protected readonly window = window;

    private readonly appSettings = inject(AppSettingsService);
    private readonly translate = inject(TranslateService);
    private readonly tenantService = inject(TenantService);
    private readonly userService = inject(UserService);

    protected readonly supportedLanguages = environment.supportedLocales;
    protected currentLanguage: string;

    public constructor() {
        console.debug(`Initializing OneGo Web Client v${buildVersion.buildVersion}...`);

        combineLatest([
            this.appSettings.appSettings,
            this.translate.onLangChange.pipe(startWith({ lang: this.translate.defaultLang })),
        ])
            .pipe(
                tap(([settings, { lang }]) => {
                    this.currentLanguage = lang;
                    this.menu = [
                        {
                            title: this.translate.instant('mapView.menu.apps'),
                            icon: 'grid_on',
                            subItems: [
                                { title: this.translate.instant('mapView.menu.applicationName'), link: ['/map'] },
                                {
                                    title: this.translate.instant('mapView.menu.myTasks'),
                                    link: settings.url.myTasks,
                                    external: true,
                                },
                            ],
                        },
                    ];
                    this.title = this.translate.instant('mapView.menu.applicationName');
                }),
                catchError(() => {
                    console.error('Failed to load app settings');
                    return EMPTY;
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }

    public ngOnInit(): void {
        // Remove the preload view
        const preloadView = document.getElementById('preload-view');
        if (preloadView) preloadView.remove();
    }

    protected onLoggedOut(): void {
        this.tenantService.clearStoredTenant().subscribe();
    }

    protected setLanguage(language: string): void {
        this.userService.saveLanguage(language);
    }
}
